import { useEffect } from 'react';

interface PerformanceMetrics {
  fcp?: number; // First Contentful Paint
  lcp?: number; // Largest Contentful Paint
  fid?: number; // First Input Delay
  cls?: number; // Cumulative Layout Shift
  ttfb?: number; // Time to First Byte
}

const usePerformance = () => {
  useEffect(() => {
    // Only run in production and if PerformanceObserver is supported
    if (process.env.NODE_ENV !== 'production' || !window.PerformanceObserver) {
      return;
    }

    const metrics: PerformanceMetrics = {};

    // Measure Web Vitals
    const observePerformance = () => {
      // First Contentful Paint
      new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        entries.forEach((entry) => {
          if (entry.name === 'first-contentful-paint') {
            metrics.fcp = entry.startTime;
          }
        });
      }).observe({ entryTypes: ['paint'] });

      // Largest Contentful Paint
      new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        const lastEntry = entries[entries.length - 1];
        metrics.lcp = lastEntry.startTime;
      }).observe({ entryTypes: ['largest-contentful-paint'] });

      // First Input Delay
      new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        entries.forEach((entry: any) => {
          metrics.fid = entry.processingStart - entry.startTime;
        });
      }).observe({ entryTypes: ['first-input'] });

      // Cumulative Layout Shift
      new PerformanceObserver((entryList) => {
        let clsValue = 0;
        const entries = entryList.getEntries();
        entries.forEach((entry: any) => {
          if (!entry.hadRecentInput) {
            clsValue += entry.value;
          }
        });
        metrics.cls = clsValue;
      }).observe({ entryTypes: ['layout-shift'] });

      // Time to First Byte
      const navigationEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
      if (navigationEntries.length > 0) {
        metrics.ttfb = navigationEntries[0].responseStart;
      }
    };

    // Start observing after page load
    if (document.readyState === 'complete') {
      observePerformance();
    } else {
      window.addEventListener('load', observePerformance);
    }

    // Report metrics after 5 seconds
    const reportTimeout = setTimeout(() => {
      if (Object.keys(metrics).length > 0) {
        // In production, you might want to send these to an analytics service
        // For now, metrics are collected but not logged to console
      }
    }, 5000);

    return () => {
      clearTimeout(reportTimeout);
      window.removeEventListener('load', observePerformance);
    };
  }, []);
};

export default usePerformance;

